const HIDDEN_CLASS = 'hidden'
const RECOVER_ACTIVE_CLASS = 'recover-form-active'
const RECOVER_HASH = '#recover'
const RECOVER_EMAIL_STORAGE_KEY = 'RECOVER_EMAIL_SELECTOR'

const selectors = {
  accountTab: '.account-tab',
  accountTabHeader: '.js-account-tab-header',
  accountForm: 'account-form-custom',
  customerLoginForm: '#customer_login',
  recoverPanel: RECOVER_HASH,
  recoverEmail: '[data-account-recover-email], .js-recover-email, input[type="email"][name="email"]',
  recoverSubmit: '[data-account-recover-submit], .js-recover-button, button[type="submit"]',
  recoverActive: '[data-account-recover-active]',
  recoverError: '.js-recover-email ~ .input-error',
  invalidField: '.is-invalid, [aria-invalid="true"]',
  trigger: 'a, button'
}

function getStorageValue(storage: Storage, key: string): string | null {
  try {
    return storage.getItem(key)
  } catch {
    return null
  }
}

function setStorageValue(storage: Storage, key: string, value: string): void {
  try {
    storage.setItem(key, value)
  } catch {
    // Storage can be unavailable in private browsing contexts.
  }
}

function showElement(element: HTMLElement | null): void {
  if (!element) return
  element.hidden = false
  element.classList.remove(HIDDEN_CLASS)

  if (element instanceof HTMLDetailsElement) {
    element.open = true
  }
}

function hideElement(element: HTMLElement | null): void {
  if (!element) return
  element.hidden = true
  element.classList.add(HIDDEN_CLASS)

  if (element instanceof HTMLDetailsElement) {
    element.open = false
  }
}

/**
 * Stores the recover email before submit and restores it after Shopify returns
 * the customer to the form with validation errors.
 */
export class AccountRecoverForm extends HTMLElement {
  private form: HTMLFormElement | null = null
  private emailInput: HTMLInputElement | null = null
  private submitButton: HTMLButtonElement | null = null

  connectedCallback() {
    this.fetchChildren()
    this.restoreEmailOnError()
    this.addEventListeners()
  }

  disconnectedCallback() {
    this.removeEventListeners()
  }

  private fetchChildren(): void {
    this.form = this.querySelector('form') || this.closest('form')
    this.emailInput = this.querySelector<HTMLInputElement>(selectors.recoverEmail)
    this.submitButton = this.querySelector<HTMLButtonElement>(selectors.recoverSubmit)
  }

  private addEventListeners(): void {
    this.form?.addEventListener('submit', this.handleSubmit)
    this.submitButton?.addEventListener('click', this.handleSubmitClick)
  }

  private removeEventListeners(): void {
    this.form?.removeEventListener('submit', this.handleSubmit)
    this.submitButton?.removeEventListener('click', this.handleSubmitClick)
  }

  private restoreEmailOnError(): void {
    if (!this.emailInput || !this.querySelector(selectors.invalidField)) return

    const savedEmail = getStorageValue(window.sessionStorage, RECOVER_EMAIL_STORAGE_KEY)
    if (savedEmail) this.emailInput.value = savedEmail
  }

  private storeEmail(): void {
    if (!this.emailInput) return

    this.querySelector(selectors.recoverError)?.remove()
    setStorageValue(window.sessionStorage, RECOVER_EMAIL_STORAGE_KEY, this.emailInput.value)
  }

  private handleSubmit = (): void => {
    this.storeEmail()
  }

  private handleSubmitClick = (): void => {
    this.storeEmail()
  }
}

/**
 * Opens the recover-password panel from a login form.
 */
export class AccountRecoverLink extends HTMLElement {
  private trigger: HTMLElement | null = null

  connectedCallback() {
    this.trigger = this.querySelector<HTMLElement>(selectors.trigger)
    this.trigger?.addEventListener('click', this.handleClick)

    if (window.location.hash === RECOVER_HASH || this.getRecoverPanel()?.querySelector(selectors.recoverActive)) {
      this.showRecoverPanel({ scrollToTop: false })
    }
  }

  disconnectedCallback() {
    this.trigger?.removeEventListener('click', this.handleClick)
  }

  private getRecoverPanel(): HTMLElement | null {
    return document.querySelector<HTMLElement>(this.dataset.recoverTarget || selectors.recoverPanel)
  }

  private showRecoverPanel({ scrollToTop = true } = {}): void {
    const accountForm = document.querySelector<HTMLElement>(this.dataset.accountFormTarget || selectors.accountForm)
    const tabHeader = document.querySelector<HTMLElement>(this.dataset.tabHeaderTarget || selectors.accountTabHeader)
    const accountTab = this.closest<HTMLElement>(selectors.accountTab)

    hideElement(accountForm)
    hideElement(tabHeader)
    showElement(this.getRecoverPanel())
    accountTab?.classList.add(RECOVER_ACTIVE_CLASS)

    if (scrollToTop) window.scroll({ top: 0, behavior: 'smooth' })
  }

  private handleClick = (event: Event): void => {
    event.preventDefault()
    this.showRecoverPanel()
  }
}

/**
 * Returns the user from the recover-password panel to the login form.
 */
export class AccountRecoverCancelLink extends HTMLElement {
  private trigger: HTMLElement | null = null

  connectedCallback() {
    this.trigger = this.querySelector<HTMLElement>(selectors.trigger)
    this.trigger?.addEventListener('click', this.handleClick)
  }

  disconnectedCallback() {
    this.trigger?.removeEventListener('click', this.handleClick)
  }

  private handleClick = (event: Event): void => {
    event.preventDefault()

    const recoverPanel = document.querySelector<HTMLElement>(this.dataset.recoverTarget || selectors.recoverPanel)
    const loginForm = document.querySelector<HTMLElement>(this.dataset.loginTarget || selectors.customerLoginForm)
    const accountForm = document.querySelector<HTMLElement>(this.dataset.accountFormTarget || selectors.accountForm)
    const tabHeader = document.querySelector<HTMLElement>(this.dataset.tabHeaderTarget || selectors.accountTabHeader)
    const accountTab = this.closest<HTMLElement>(selectors.accountTab)

    hideElement(recoverPanel)
    showElement(loginForm)
    showElement(accountForm)
    showElement(tabHeader)
    accountTab?.classList.remove(RECOVER_ACTIVE_CLASS)
  }
}
